/**
 * operationApprovedUpdate.js (C13, §8.7) — cuándo un nodo vinculado debe avisar de que hay una
 * versión más nueva de su operación (RN-05.1 + RN-05.2, S85/S86/S105).
 *
 * La condición exacta del contrato: existe una versión con número **estrictamente mayor** que el
 * vinculado y **estrictamente mayor** que el descartado, si hay descarte. El descarte es del
 * VÍNCULO, no una preferencia del usuario: por eso vive en `NodeOperationLink` y no en el cliente
 * (S107).
 *
 * Sun ya calcula `updateAvailable` (§8.4) y es la autoridad; este módulo existe porque la misma
 * regla la necesitan el banner del nodo (C5) y el indicador del listado (C8), que leen dos fuentes
 * distintas —N13 y la fila de `getWorkflows`— y no pueden discrepar entre sí.
 *
 * Puro a propósito: lo que cuenta como actualización es una regla, no un detalle de pintado.
 */

/**
 * @param {object} link el `NodeOperationLink` del nodo (§8.4)
 * @returns {boolean} si hay que avisar de una versión más nueva
 */
export const hasOperationUpdate = (link = {}) => {
  if (!link?.linked) return false
  const linked = Number(link.versionNumber || 0)
  const dismissed = Number(link.dismissedVersionNumber || 0)
  const latest = Number(link.latestVersionNumber || 0)
  return latest > Math.max(linked, dismissed)
}

/**
 * La versión que se ofrece: la más alta que Sun conoce. Es la que anuncia el banner, la que se
 * registra al descartar (N15) y a la que se pide actualizar (N14 + guardado), de modo que las tres
 * no puedan separarse.
 *
 * @returns {object} `{nodeId, versionNumber}` de la versión candidata, o `{}` si no hay ninguna
 */
export const resolveOperationUpdate = (link = {}) => {
  if (!hasOperationUpdate(link)) return {}
  return { nodeId: link.latestNodeId || '', versionNumber: Number(link.latestVersionNumber || 0) }
}

/**
 * ¿Se está usando una versión anterior a propósito? Es lo que queda tras descartar (K42, S105): el
 * nodo sigue en su versión y el aviso ya no interrumpe.
 */
export const isUsingOlderVersion = (link = {}) => {
  if (!link?.linked) return false
  const dismissed = Number(link.dismissedVersionNumber || 0)
  return dismissed > Number(link.versionNumber || 0)
}

/**
 * La condición aplicada a una fila del listado de workflows (`operationUpdateAvailable`, §8.5). El
 * campo ausente se trata como false, y solo lo puebla `POST getWorkflows` — ni
 * `getAllTemplateWorkflows` ni `getWorkflowVersions` (asimetría deliberada, `contrato-sun.md` §7).
 */
export const rowHasOperationUpdate = (row = {}) => row?.operationUpdateAvailable === true
